import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * Save-status indicator for the shared useAutoSave hook. Replaces manual Save
 * buttons with a Google-Docs style status line.
 *
 * Maps the hook status to the spec's user-facing states:
 *   saving   → "Saving…"
 *   saved    → "Saved just now"  (recent) → "All changes saved" (after ~4s)
 *   error    → "Unable to save changes"   /  "Retrying…" while a retry is queued
 *
 * @param {string}  status         Hook status: idle | saving | saved | error | retrying.
 * @param {Date}    [lastSavedAt]  Timestamp of the last successful save.
 * @param {boolean} [isOnline=true]
 */
const AutoSaveIndicator = ({
  status,
  lastSavedAt,
  isOnline = true,
  draft = false,
  className = "",
  style = {},
}) => {
  const { t } = useTranslation();
  // Flip "Saved just now" → "All changes saved" a few seconds after a save.
  const [recentlySaved, setRecentlySaved] = useState(false);

  useEffect(() => {
    if (status === "saved") {
      setRecentlySaved(true);
      const timer = setTimeout(() => setRecentlySaved(false), 4000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [status, lastSavedAt]);

  let text = null;
  let color = "#6B7280";

  // `draft` mode is used on Create/Generate forms, where autosave only keeps a
  // draft of the inputs (it does NOT create the real record). The wording makes
  // that explicit so "Saving/Saved" is never mistaken for "the item was created".
  if (status === "saving") {
    text = draft ? t("Saving draft…") : t("Saving…");
  } else if (status === "retrying") {
    text = t("Retrying…");
    color = "#d97706";
  } else if (status === "error") {
    text = isOnline
      ? draft
        ? t("Draft not saved")
        : t("Unable to save changes")
      : t("Offline — changes will save when reconnected");
    color = "#dc2626";
  } else if (status === "saved") {
    if (draft) {
      text = t("Draft kept");
    } else {
      text = recentlySaved ? t("Saved just now") : t("All changes saved");
    }
    color = "#16a34a";
  }

  if (!text) return null; // idle → render nothing

  return (
    <span
      className={`small ${className}`}
      style={{ fontSize: "0.8rem", color, ...style }}
      aria-live="polite"
    >
      {text}
    </span>
  );
};

export default AutoSaveIndicator;
